import {Component, Inject, OnInit} from '@angular/core';
import {Team} from '../../admin/models/team.model';
import {Employee} from '../../admin/models/employee.model';
import {HrService} from '../hr.service';
import {FabMessageService} from '@fabric/components/message';
import {PersonalService} from '../../personal/personal.service';
import {AuthenticationService} from '../../core/authentication/authentication.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {map, Observable, shareReplay} from 'rxjs';

@Component({
    selector: 'app-edit-team-pop-up',
    templateUrl: './edit-team-pop-up.component.html',
    styleUrls: ['./edit-team-pop-up.component.scss']
})
export class EditTeamPopUpComponent implements OnInit {

    public peopleSearchResults: any [] = [];
    public altSearchResults: any [] = [];
    public selectedPerson: any;
    public altSelectedPerson: any;
    public searchSubscription: any;
    public model = {} as Employee;
    public manager: any;
    public alternateManager: any;
    public teamDetails = {} as Team;
    public isPageLoading = false;

    constructor(
        private readonly hrService: HrService,
        private readonly fabMessageService: FabMessageService,
        private readonly personalService: PersonalService,
        private readonly authService: AuthenticationService,
        public dialogRef: MatDialogRef<[]>,
        @Inject(MAT_DIALOG_DATA) public selectedData: any) {
    }

    ngOnInit(): void {
        this.teamDetails = this.selectedData;
        this.getTeamDetails(this.teamDetails);
        this.teamDetails.updatedBy = this.authService.userInfo.gmin || '';
    }

    public getTeamDetails(value: Team) {
        this.getEmployeeDisplayName(value.manager).subscribe({
            next: (managerName) => {
                this.manager = managerName;
            },
        });
        this.getEmployeeDisplayName(value.alternateManager).subscribe({
            next: (altManagerName) => {
                this.alternateManager = altManagerName;
            },
        });
    }

    /**
     * Show employee name instead of gmin.
     * @param manager
     */
    public getEmployeeDisplayName(manager: string): Observable<any> {
        return this.hrService.searchForEmployees(manager).pipe(map(response => {
            if (response && response.body && response.body.length > 0) {
                const employee = response.body[0];
                employee.displayName = `${employee.firstName} ${employee.lastName}`;

                shareReplay(1);
                return employee.displayName;
            } else {
                {
                    this.fabMessageService.displayErrorMessage('Employee Not Found');
                }
            }
        }));
    }

    /**
     * Submits the form to modify a teams details.
     */
    submitForm(): void {
        this.isPageLoading = true;
        this.hrService.updateTeam(this.teamDetails).subscribe({
            next: () => {
                this.fabMessageService.displayMessage('Request submitted successfully');
                this.close();
                this.isPageLoading = false;
            }, error: () => {
                this.fabMessageService.displayErrorMessage('Request submission failed');
                this.isPageLoading = false;
            }
        });
    }

    /**
     * Closes the popup
     */
    close(): void {
        this.dialogRef.close();
    }

    /**
     * This will call the search method once the user has populated
     * the search box with 4 characters.
     */
    onAddMemberChanged() {
        this.selectedPerson = undefined;
        const minimumLengthToSearch = 3;
        if (this.manager.length < minimumLengthToSearch) {
            this.peopleSearchResults = [];
        }
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        if (this.manager.length > minimumLengthToSearch) {
            this.search();
        }
    }

    /**
     * This will call the search method once the user has populated
     * the search box with 4 characters.
     */
    onAddAltMemberChanged() {
        this.altSelectedPerson = undefined;
        const minimumLengthToSearch = 3;
        if (this.alternateManager.length < minimumLengthToSearch) {
            this.altSearchResults = [];
        }
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        if (this.alternateManager.length > minimumLengthToSearch) {
            this.altSearch();
        }
    }

    /**
     * This uses the result of the search function to
     * populate the form with the persons details.
     * @param person the person to populate the form
     */
    personSelected(person: any) {
        this.manager = (person.displayName);
        this.peopleSearchResults = [];
        this.selectedPerson = person;
        this.teamDetails.manager = this.selectedPerson.gmin;
        this.teamDetails.managerEmail = this.selectedPerson.email;
    }

    /**
     * This uses the result of the search function to
     * populate the form with the persons details.
     * @param person the person to populate the form
     */
    altPersonSelected(person: any) {
        this.alternateManager = (person.displayName);
        this.altSearchResults = [];
        this.altSelectedPerson = person;
        this.teamDetails.alternateManager = this.altSelectedPerson.gmin;
        this.teamDetails.alternateManagerEmail = this.altSelectedPerson.email;
    }

    /**
     * Calls the search for employees method in the hr service
     * returns an error response if it is not working
     * @private
     */
    private search() {
        this.searchSubscription = this.hrService.searchForEmployees(this.manager).subscribe({
            next: response => {
                if (response.body !== null) {
                    this.peopleSearchResults = response.body;
                }
            },
            error: () => {
                this.fabMessageService.displayErrorMessage('PeopleService Unavailable');
            }
        });
    }

    /**
     * Calls the search for employees method in the hr service
     * returns an error response if it is not working
     * @private
     */
    private altSearch() {
        this.searchSubscription = this.hrService.searchForEmployees(this.alternateManager).subscribe({
            next: response => {
                if (response.body !== null) {
                    this.altSearchResults = response.body;
                }
            },
            error: () => {
                this.fabMessageService.displayErrorMessage('PeopleService Unavailable');
            }
        });
    }

    /**
     * makes a call to a method in the hr service to get photo Url
     * @param userGmin id used to locate photo
     */
    getPhoto(userGmin: string): string {
        return this.hrService.getPhotoUrl(userGmin);
    }

    /**
     * makes a call to a method in the hr service to display the default image
     * @param event onclick or keyup
     */
    displayDefaultImage(event: any) {
        this.hrService.displayDefaultImage(event);
    }
}
